/* istanbul ignore file */

import { logErrorToApplicationInsights, useRequestInit } from "adviesbox-shared";
import { FormikContextType } from "formik";
import produce, { applyPatches, Patch, produceWithPatches } from "immer";
import memoize from "lodash/memoize";
import pDebounce from "p-debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import isEqual from "react-fast-compare";
import { RouteParams } from "../../paramrouting/paramrouting-context";
import { hasJSONResponse } from "../../utils/ajax";
import { setFormikUiErrors } from "../../utils/set-formik-ui-errors";
import { validationResultsToUiError } from "../../utils/validation-results-to-ui-error";
import { createSubBag } from "./create-isw-helpers";
import { hasChanged } from "./has-changed-helper";
import {
  forceReturnContext,
  ISWAsyncCondition,
  ISWAsyncContext,
  ISWAsyncSideEffectBag,
  ISWFetchDataParams,
  ISWSideEffect,
  ISWSideEffectWithContext
} from "./isw-types";

const isDev = process.env.NODE_ENV === "development";

let lastMemo: any;
const debounceAsyncSideEffect = memoize(
  <T, X>(context: ISWAsyncContext<T, X>, asyncDebounce: number) => {
    if (lastMemo) /* istanbul ignore next */ clearTimeout(lastMemo);
    lastMemo = setTimeout(
      /* istanbul ignore next */ () => (debounceAsyncSideEffect.cache = new memoize.Cache()),
      asyncDebounce
    );

    return pDebounce(async (bag: ISWAsyncSideEffectBag<T, X>) => {
      return await context.asyncSideEffect(bag);
    }, asyncDebounce);
  },
  context => contextToKey(context)
);

export const useISWSideEffects = <T extends {}>(
  currentFormik: FormikContextType<T>,
  syncEffect?: ISWSideEffect<T> | ISWSideEffectWithContext<T, any>,
  asyncEffect?: ISWAsyncCondition<T>,
  runOnFirstRender?: boolean,
  disablePlatformApiFoutenHandler?: boolean,
  asyncStartStopCallback?: React.Dispatch<React.SetStateAction<boolean>>
): void => {
  const { requestInit, params, settings, user } = useRequestInit<RouteParams>({
    extraHeaders: { "Content-Type": "application/json;charset=UTF-8" }
  });

  const getSyncContext = useCallback(() => (syncEffect ? syncEffect(forceReturnContext as any) : null), [syncEffect]);
  const getASyncContext = useCallback(() => (asyncEffect ? asyncEffect(forceReturnContext as any) : null), [
    asyncEffect
  ]);

  const previous = useRef(currentFormik.values);
  const previousSyncContext = useRef<any>(getSyncContext());
  const previousASyncContext = useRef<any>(getASyncContext());
  const abortControllers = useRef<{ [index: string]: AbortController }>({});
  const mounted = useRef<boolean>(true);
  const platformApiFoutenUpdate = useRef<boolean>(false);
  const patchSets = useRef<{ patches: Patch[]; signal: AbortSignal }[]>([]);
  const inverseSets = useRef<Patch[][]>([]);
  const [, rerender] = useState({});
  const firstRender = useRef(true);
  const runBecauseFirstRender = firstRender.current && runOnFirstRender;
  const isAsyncRunning = useRef(false);
  const lastAppliedPatchTimestamp = useRef<number>(0);

  useEffect(() => {
    mounted.current = true;

    const currentSyncContext = getSyncContext();
    const hasSyncContextChanges = !isEqual(previousSyncContext.current, currentSyncContext);
    previousSyncContext.current = currentSyncContext;

    const currentASyncContext = getASyncContext();
    const hasASyncContextChanges = !isEqual(previousASyncContext.current, currentASyncContext);
    previousASyncContext.current = currentASyncContext;

    const hasFormikStateChanges = previous.current !== currentFormik.values;
    const hasPatches = patchSets.current.length > 0;

    isDev && devlog("Effect triggered with state:", {
      hasFormikStateChanges,
      hasSyncContextChanges,
      hasASyncContextChanges,
      hasPatches,
      isAsyncRunning: isAsyncRunning.current,
      runBecauseFirstRender
    });

    if (platformApiFoutenUpdate.current) {
      devlog("Skipping sideEffects because platformApiFoutenUpdate");
      platformApiFoutenUpdate.current = false;
      return;
    }

    if (
      !runBecauseFirstRender &&
      !hasFormikStateChanges &&
      !hasPatches &&
      !hasSyncContextChanges &&
      !hasASyncContextChanges
    ) {
      return;
    }

    let mustSave = false;
    let appliedPatches = false;
    let next = currentFormik.values;

    if (hasPatches) {
      const currentTime = Date.now();
      if (currentTime - lastAppliedPatchTimestamp.current > 100) {
        for (let i = patchSets.current.length - 1; i >= 0; i--) {
          try {
            if (!patchSets.current[i].signal.aborted) {
              isDev && devlog("applying async patchset", JSON.stringify(merge(patchSets.current[i].patches, inverseSets.current[i])));
              next = applyPatches(next, patchSets.current[i].patches);
              mustSave = true;
              lastAppliedPatchTimestamp.current = currentTime;
            }
          } catch (err) {
            isDev && deverror("unexpected error while applying async patchset", JSON.stringify(patchSets.current[i]), err);
            logErrorToApplicationInsights(err);
          }
        }

        appliedPatches = mustSave;
        patchSets.current = [];
        inverseSets.current = [];
        if (appliedPatches) {
          next = { ...next, platformApiFouten: null };
        }
        asyncStartStopCallback && asyncStartStopCallback(false);
        isAsyncRunning.current = false;
      }
    }

    const shouldRunSync = syncEffect && (
      runBecauseFirstRender || 
      hasFormikStateChanges || 
      hasSyncContextChanges || 
      appliedPatches
    );

    isDev && devlog("Sync effect evaluation:", {
      shouldRunSync,
      syncEffect: !!syncEffect,
      isAsyncRunning: isAsyncRunning.current
    });

    if (shouldRunSync) {
      isDev && devlog("running sync effect", {
        runBecauseFirstRender,
        hasFormikStateChanges,
        hasSyncContextChanges,
        appliedPatches,
        next
      });
      
      const prev = previous.current;
      const [result, changes, inv] = produceWithPatches(next, draft => {
        const has = hasChanged<T>(draft as T, prev);
        const bag = { has, draft, prev, init: currentFormik.initialValues, context: undefined };
        syncEffect && syncEffect({ ...bag, subset: createSubBag([], bag) });
      });
      
      if (changes.length) {
        isDev && devlog("applying sync patchset", JSON.stringify(merge(changes, inv)));
        next = result;
        mustSave = true;
      } else {
        isDev && devlog("sync effect produced no changes");
      }
    }

    if (
      asyncEffect &&
      (runBecauseFirstRender || hasFormikStateChanges || hasASyncContextChanges || mustSave) &&
      !appliedPatches
    ) {
      asyncStartStopCallback && asyncStartStopCallback(true);
      isAsyncRunning.current = true;
      
      const prev = { ...previous.current };
      const hasSync = hasChanged<T>(next, prev);
      let runAsyncUsed = false;
      const runAsync = (asyncWithContext: ISWAsyncContext<T, any>): void => {
        runAsyncUsed = true;
        const key = contextToKey(asyncWithContext);
        if (abortControllers.current[key]) {
          abortControllers.current[key].abort();
        }
        abortControllers.current[key] = new AbortController();
        const signal = abortControllers.current[key].signal;

        /* eslint-disable-next-line @typescript-eslint/no-floating-promises */
        (async (): Promise<void> => {
          try {
            const debouncedAsyncSideEffect = debounceAsyncSideEffect(asyncWithContext, +settings.asyncDebounce);
            let patches: Patch[] = [];
            let inverse: Patch[] = [];
            await produce(
              next,
              async draft => {
                const hasAsync = hasChanged<T>(draft as T, prev);
                const fetchData = fetchDataHelper.bind({
                  requestInit: { ...requestInit, signal },
                  data: draft,
                  formik: currentFormik,
                  mounted,
                  disablePlatformApiFoutenHandler
                }) as typeof fetchDataHelper;
                const bag = {
                  has: hasAsync,
                  draft,
                  prev,
                  init: currentFormik.initialValues,
                  signal,
                  fetchData,
                  params,
                  settings,
                  user,
                  context: asyncWithContext.context,
                  formik: currentFormik
                };
                await debouncedAsyncSideEffect(bag);
              },
              (p, i) => {
                if (p.length && !signal.aborted) {
                  patches = patches.concat(p);
                  inverse = inverse.concat(i);
                } else {
                  asyncStartStopCallback && asyncStartStopCallback(false);
                }
              }
            );

            if (patches.length && !signal.aborted) {
              patchSets.current.push({ patches, signal });
              inverseSets.current.push(inverse);
              if (mounted.current) rerender({});
            }
          } catch (err) {
            if (err.name === "AsyncSideEffectError") platformApiFoutenUpdate.current = true;

            if (err.name !== "AbortError" && err.name !== "AsyncSideEffectError") {
              isDev && deverror("unexpected error in async sideeffect:", asyncWithContext, err);
              logErrorToApplicationInsights(err);
            }
          }
        })();
      };
      asyncEffect({
        has: hasSync,
        curr: next,
        prev,
        init: currentFormik.initialValues,
        context: undefined,
        runAsync
      });

      if (!runAsyncUsed) {
        asyncStartStopCallback && asyncStartStopCallback(false);
      }
    }

    if (mustSave) {
      isDev && devlog("saving new values", next);
      currentFormik.setValues(next, true);
    }
    previous.current = next;
    firstRender.current = false;

    return (): void => {
      mounted.current = false;
    };
  }, [
    asyncStartStopCallback,
    currentFormik,
    syncEffect,
    asyncEffect,
    requestInit,
    params,
    settings,
    user,
    rerender,
    getSyncContext,
    getASyncContext,
    runBecauseFirstRender,
    disablePlatformApiFoutenHandler
  ]);
};

function contextToKey<T, X>({ id, context }: ISWAsyncContext<T, X>): string {
  return JSON.stringify({
    id,
    context
  });
}

class AsyncSideEffectError extends Error {
  constructor(...params: any[]) {
    super(...params);
    this.name = "AsyncSideEffectError";
  }
}
async function fetchDataHelper<T, B extends { call?: never }, R>(
  this: {
    requestInit: RequestInit;
    data: T;
    formik: FormikContextType<any>;
    mounted: React.MutableRefObject<boolean>;
    disablePlatformApiFoutenHandler?: boolean;
  },
  { url, method, body, extraHeaders, mapperDlNameToUiName }: ISWFetchDataParams<T, B>
): Promise<R> {
  if (typeof body === "function") body = body(this.data);

  if (typeof body === "object" && body !== null) {
    body = JSON.stringify(body);
  } else if (typeof body !== "string") {
    body = undefined;
  }

  const headers = {
    ...this.requestInit.headers,
    ...extraHeaders
  };

  const rsp = await fetch(url, {
    ...this.requestInit,
    headers: headers,
    method: method ?? "POST",
    body: body
  });

  if (rsp.ok && rsp.status === 204) return undefined as any;

  if (!hasJSONResponse(rsp)) {
    if (!this.mounted.current) throw new Error("fout bij tonen van foutmelding: component not mounted");

    const label = `Fout bij aanroep van web-api '${url}'`;
    const error = `Resultaat met status ${rsp.status} bevat geen JSON`;
    setFormikUiErrors([{ field: null, label, error }], this.formik);
    throw new AsyncSideEffectError(label + ": " + error);
  }

  const json = await rsp.json();
  if ((!rsp.ok || json.isValid === false) && !this.disablePlatformApiFoutenHandler) {
    if (!this.mounted.current) throw new Error("fout bij tonen validationResults: component not mounted");

    if (json.validationResults instanceof Array) {
      setFormikUiErrors(
        validationResultsToUiError(json.validationResults, mapperDlNameToUiName ?? (() => null)),
        this.formik
      );
      throw new AsyncSideEffectError("platform fout");
    } else {
      const label = `Fout bij aanroep van web-api '${url}'`;
      const error = `Resultaat met status ${rsp.status} bevat geen validationResults`;
      setFormikUiErrors([{ field: null, label, error }], this.formik);
      throw new AsyncSideEffectError(label + ": " + error);
    }
  }

  return json;
}

type MergedPatch = Patch & { prev?: any };
function merge(patches: Patch[], inverse: Patch[]): MergedPatch[] {
  const merged: MergedPatch[] = [];
  for (let i = 0; i < patches.length; i++) {
    if (patches[i].path === inverse[i].path) {
      merged[i] = { ...patches[i], prev: inverse[i].value };
    } else {
      /* istanbul ignore next */
      isDev && deverror("patch/inverse patch mismatch: expecting immer to return matching indexes for patch/inverse");
      /* istanbul ignore next */
      return patches;
    }
  }

  return merged;
}

/*#__PURE__*/
function devlog(...args: any): void {
  /* eslint-disable-next-line no-console */
  if (process.env.NODE_ENV === "development") console.log.apply(undefined, args);
}
/* istanbul ignore next */
/*#__PURE__*/
function deverror(...args: any): void {
  /* eslint-disable-next-line no-console */
  if (process.env.NODE_ENV === "development") console.log.apply(undefined, args);
}
